<script setup lang="ts">
import { useLocalStorage } from "@vueuse/core";
import { Collapse } from "vue-collapsed";
import type { AnnouncementRepresentation } from "../../types";
import {
  useRoute,
  useAnnouncementDataState,
  useAnnouncementResource,
  computed,
  onMounted,
  ref,
  watch,
  useCurrentCulture,
} from "#imports";

const props = withDefaults( defineProps<{
  variant?: string;
  alignButtons?: "end";
  buttonSize?: "sm" | "md" | "lg";
  buttonLook?: string;
}>(), {
  variant: undefined,
  alignButtons: undefined,
  buttonSize: "sm",
  buttonLook: "outline",
} );

const culture = useCurrentCulture();

const { getForUrl } = useAnnouncementResource();
const route = useRoute();

const announcements = useAnnouncementDataState();

watch( culture, async () => {
  const data = await getForUrl( route.fullPath );
  if ( data.data.value ) {
    announcements.value = data.data.value;
  }
} );

const anyDismissible = computed( () => {
  return visibleAnnouncements.value.some( x => x.dismissible );
} );

const ready = ref( false );

const storageState = useLocalStorage<string[]>( "apollo-announcements", [] );

const visibleAnnouncements = computed<AnnouncementRepresentation[]>( () => {
  if ( !ready.value ) {
    return [];
  }

  return announcements.value?.filter( x => !storageState.value.includes( x.key ) ) || [];
} );

function onDismiss ( key: string ) {
  if ( !storageState.value.includes( key ) ) {
    storageState.value.push( key );
  }
}

function getAnnouncementVariant () {
  return props.variant || "primary";
}

onMounted( () => {
  ready.value = true;
} );
</script>

<template>
  <Collapse
    :when="ready && visibleAnnouncements.length > 0"
    class="v-collapse">
    <ul
      class="announcements"
      :class="anyDismissible ? 'announcements--has-dismissible' : ''">
      <li
        v-for="(announcement) in visibleAnnouncements"
        :key="announcement.key"
        class="announcements__item">
        <a-alert
          :icon="announcement.icon"
          :dismissible="announcement.dismissible"
          :variant="getAnnouncementVariant()"
          class="announcement"
          :class="alignButtons ? 'announcement--align-buttons-' + alignButtons : ''"
          body-class="container"
          @dismiss="onDismiss(announcement.key)">
          <div class="announcement__body">
            <rich-text
              class="announcement__content"
              :content="announcement.content" />

            <ul class="announcement__actions">
              <li
                v-for="(action) in announcement.actions"
                :key="action.key"
                class="announcement__action">
                <a-button
                  :to="action.url"
                  :target="action.target"
                  :size="buttonSize"
                  :look="buttonLook"
                  :variant="getAnnouncementVariant()">
                  {{ action.label }}
                  <a-icon
                    v-if="action.openInNewTab"
                    icon="fa:solid:arrow-up-right" />
                </a-button>
              </li>
            </ul>
          </div>
        </a-alert>
      </li>
    </ul>
  </Collapse>
</template>

<style scoped lang="scss">
.announcements {
  margin: 0;
  padding: 0;
  list-style: none;

  &--has-dismissible {
    ::v-deep( .alert__dismiss-wrapper ) {
      display: flex;
    }
  }
}

.announcement {
  border-radius: 0;
  border-left: 0;
  border-right: 0;

  &:first-child {
    border-top: 0;
  }

  &__body {
    display: flex;
    gap: var(--s-4);
    align-items: center;
    flex-wrap: wrap;
  }

  &__content {
    display: inline-block;

    ::v-deep( p:last-child ) {
      margin: 0;
    }
  }

  &__actions {
    margin: 0;
    padding: 0;
    list-style: none;
    display: inline-flex;
    gap: var( --s-4 );

    @media(max-width: 767px) {
      flex-direction: column;
      min-width: 100%;

      :deep(.btn) {
        width: 100%;
      }
    }
  }

  &--align-buttons-end &__content {
    flex-grow: 1;
  }
}

.v-collapse {
  transition: height var(--vc-auto-duration) cubic-bezier(0.33, 1, 0.68, 1);
}
</style>
